import React from 'react';
import { 
  subscribe as subSecurity, 
  updatePasswordPolicy, 
  updateSessionSecurity, 
  updateTwoFactorAuth, 
  updateApiSecurity, 
  updateMonitoring, 
  updateCompliance,
  resetToDefaults,
  getSecurityScore,
  validatePasswordPolicy
} from '../../../services/shared/securityService';

function SecuritySection({ title, description, children }){
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, description }){
  return (
    <label className="flex items-start gap-3 py-2">
      <input type="checkbox" checked={checked} onChange={e=> onChange(e.target.checked)} className="mt-1 w-4 h-4 text-purple-600 rounded border-slate-300 focus:ring-purple-500" />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-700">{label}</div>
        {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
      </div>
    </label>
  );
}

function NumberInput({ label, value, onChange, min, max, suffix }){
  return (
    <label className="block py-2">
      <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input 
          type="number" 
          value={value} 
          onChange={e=> onChange(Number(e.target.value))} 
          min={min} 
          max={max}
          className="w-24 h-8 px-2 rounded border border-slate-300 text-sm" 
        />
        {suffix && <span className="text-sm text-slate-500">{suffix}</span>}
      </div>
    </label>
  );
}

function SelectInput({ label, value, onChange, options }){
  return (
    <label className="block py-2">
      <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
      <select value={value} onChange={e=> onChange(e.target.value)} className="h-8 px-2 rounded border border-slate-300 text-sm">
        {options.map(opt=> <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </label>
  );
}

function SecurityScoreCard({ score }){
  const getColor = (s) => {
    if(s >= 90) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if(s >= 70) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-rose-600 bg-rose-50 border-rose-200';
  };
  
  return (
    <div className={`rounded-lg border p-4 ${getColor(score)}`}>
      <div className="text-sm font-medium mb-1">Security Score</div>
      <div className="text-2xl font-bold">{score}/100</div>
      <div className="text-xs mt-1">
        {score >= 90 ? 'Excellent' : score >= 70 ? 'Good' : 'Needs Improvement'}
      </div>
    </div>
  );
}

export default function SecuritySettings(){
  const [settings, setSettings] = React.useState(null);
  const [testPassword, setTestPassword] = React.useState('');
  const [passwordValidation, setPasswordValidation] = React.useState(null);
  
  React.useEffect(()=> subSecurity(setSettings), []);
  
  const score = React.useMemo(()=> getSecurityScore(), [settings]);
  
  function testPasswordStrength(){
    if(!testPassword.trim()) return;
    const result = validatePasswordPolicy(testPassword);
    setPasswordValidation(result);
  }
  
  if(!settings) return <div className="p-6">Loading...</div>;
  
  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Security Settings</h1>
          <p className="text-slate-600 mt-1">Configure authentication, authorization, and security policies</p>
        </div>
        <div className="flex items-center gap-4">
          <SecurityScoreCard score={score} />
          <button onClick={resetToDefaults} className="h-9 px-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Reset to Defaults</button>
        </div>
      </div>
      
      <SecuritySection title="Password Policy" description="Define password requirements and account lockout rules">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberInput 
            label="Minimum Length" 
            value={settings.passwordPolicy.minLength} 
            onChange={v=> updatePasswordPolicy({ minLength: v })}
            min={8}
            max={50}
            suffix="characters"
          />
          <NumberInput 
            label="Lockout Threshold" 
            value={settings.passwordPolicy.lockoutThreshold} 
            onChange={v=> updatePasswordPolicy({ lockoutThreshold: v })}
            min={3}
            max={20}
            suffix="failed attempts"
          />
          <NumberInput 
            label="Lockout Duration" 
            value={settings.passwordPolicy.lockoutDuration} 
            onChange={v=> updatePasswordPolicy({ lockoutDuration: v })}
            min={5}
            max={1440}
            suffix="minutes"
          />
          <NumberInput 
            label="Password Max Age" 
            value={settings.passwordPolicy.maxAge} 
            onChange={v=> updatePasswordPolicy({ maxAge: v })}
            min={30}
            max={365}
            suffix="days"
          />
          <NumberInput 
            label="Prevent Reuse" 
            value={settings.passwordPolicy.preventPasswordReuse} 
            onChange={v=> updatePasswordPolicy({ preventPasswordReuse: v })}
            min={0}
            max={24}
            suffix="last passwords"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="Require Uppercase Letters" 
              checked={settings.passwordPolicy.requireUppercase}
              onChange={v=> updatePasswordPolicy({ requireUppercase: v })}
            />
            <Toggle 
              label="Require Lowercase Letters" 
              checked={settings.passwordPolicy.requireLowercase}
              onChange={v=> updatePasswordPolicy({ requireLowercase: v })}
            />
            <Toggle 
              label="Require Numbers" 
              checked={settings.passwordPolicy.requireNumbers}
              onChange={v=> updatePasswordPolicy({ requireNumbers: v })}
            />
          </div>
          <div>
            <Toggle 
              label="Require Special Characters" 
              checked={settings.passwordPolicy.requireSpecialChars}
              onChange={v=> updatePasswordPolicy({ requireSpecialChars: v })}
            />
            <Toggle 
              label="Prevent Common Passwords" 
              checked={settings.passwordPolicy.preventCommonPasswords}
              onChange={v=> updatePasswordPolicy({ preventCommonPasswords: v })}
              description="Block dictionary words and common patterns"
            />
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-slate-50 rounded border border-slate-200">
          <div className="text-sm font-medium text-slate-700 mb-2">Test Password Strength</div>
          <div className="flex gap-2">
            <input 
              type="password" 
              value={testPassword} 
              onChange={e=> setTestPassword(e.target.value)}
              placeholder="Enter password to test..."
              className="flex-1 h-8 px-2 rounded border border-slate-300 text-sm"
            />
            <button onClick={testPasswordStrength} className="h-8 px-3 rounded bg-purple-600 text-white text-sm hover:bg-purple-500">Test</button>
          </div>
          {passwordValidation && (
            <div className={`mt-2 text-sm ${passwordValidation.valid ? 'text-emerald-600' : 'text-rose-600'}`}>
              {passwordValidation.valid ? '✓ Password meets all requirements' : passwordValidation.errors.join(', ')}
            </div>
          )}
        </div>
      </SecuritySection>
      
      <SecuritySection title="Session Security" description="Configure session timeouts and concurrent access">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberInput 
            label="Session Timeout" 
            value={settings.sessionSecurity.sessionTimeout} 
            onChange={v=> updateSessionSecurity({ sessionTimeout: v })}
            min={15}
            max={1440}
            suffix="minutes"
          />
          <NumberInput 
            label="Max Concurrent Sessions" 
            value={settings.sessionSecurity.maxConcurrentSessions} 
            onChange={v=> updateSessionSecurity({ maxConcurrentSessions: v })}
            min={1}
            max={10}
            suffix="sessions"
          />
          <NumberInput 
            label="Remember Me Duration" 
            value={settings.sessionSecurity.rememberMeDuration} 
            onChange={v=> updateSessionSecurity({ rememberMeDuration: v })}
            min={1}
            max={90}
            suffix="days"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="Require Re-auth for Sensitive Actions" 
              checked={settings.sessionSecurity.requireReauthForSensitive}
              onChange={v=> updateSessionSecurity({ requireReauthForSensitive: v })}
              description="Require password for security changes"
            />
            <Toggle 
              label="Logout on Browser Close" 
              checked={settings.sessionSecurity.logoutOnClose}
              onChange={v=> updateSessionSecurity({ logoutOnClose: v })}
            />
          </div>
          <div>
            <Toggle 
              label="Allow Remember Me" 
              checked={settings.sessionSecurity.rememberMe}
              onChange={v=> updateSessionSecurity({ rememberMe: v })}
              description="Let users stay logged in longer"
            />
          </div>
        </div>
      </SecuritySection>
      
      <SecuritySection title="Two-Factor Authentication" description="Configure multi-factor authentication requirements">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="Enable Two-Factor Authentication" 
              checked={settings.twoFactorAuth.enabled}
              onChange={v=> updateTwoFactorAuth({ enabled: v })}
              description="Require second factor for all users"
            />
            <Toggle 
              label="Enforce for Administrators" 
              checked={settings.twoFactorAuth.enforceForAdmins}
              onChange={v=> updateTwoFactorAuth({ enforceForAdmins: v })}
              description="Always require 2FA for admin roles"
            />
            <Toggle 
              label="Backup Codes" 
              checked={settings.twoFactorAuth.backupCodes}
              onChange={v=> updateTwoFactorAuth({ backupCodes: v })}
              description="Generate one-time recovery codes"
            />
          </div>
          <div>
            <NumberInput 
              label="Grace Period for New Users" 
              value={settings.twoFactorAuth.gracePeriod} 
              onChange={v=> updateTwoFactorAuth({ gracePeriod: v })}
              min={0}
              max={30}
              suffix="days"
            />
            <div className="py-2">
              <div className="text-sm font-medium text-slate-700 mb-2">Allowed Methods</div>
              <div className="text-xs text-slate-500">TOTP (Authenticator), SMS, Email</div>
            </div>
          </div>
        </div>
      </SecuritySection>
      
      <SecuritySection title="API Security" description="Configure API access and rate limiting">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberInput 
            label="Key Rotation Interval" 
            value={settings.apiSecurity.keyRotationInterval} 
            onChange={v=> updateApiSecurity({ keyRotationInterval: v })}
            min={30}
            max={365}
            suffix="days"
          />
          <NumberInput 
            label="Max Keys per User" 
            value={settings.apiSecurity.maxKeysPerUser} 
            onChange={v=> updateApiSecurity({ maxKeysPerUser: v })}
            min={1}
            max={20}
          />
          <NumberInput 
            label="Rate Limit" 
            value={settings.apiSecurity.rateLimit} 
            onChange={v=> updateApiSecurity({ rateLimit: v })}
            min={100}
            max={10000}
            suffix="requests/hour"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="Require HTTPS" 
              checked={settings.apiSecurity.requireHttps}
              onChange={v=> updateApiSecurity({ requireHttps: v })}
              description="Block non-encrypted API requests"
            />
          </div>
          <div>
            <Toggle 
              label="IP Whitelisting" 
              checked={settings.apiSecurity.ipWhitelisting}
              onChange={v=> updateApiSecurity({ ipWhitelisting: v })}
              description="Restrict API access by IP address"
            />
          </div>
        </div>
      </SecuritySection>
      
      <SecuritySection title="Security Monitoring" description="Configure logging and alerting">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="Log Failed Login Attempts" 
              checked={settings.monitoring.logFailedLogins}
              onChange={v=> updateMonitoring({ logFailedLogins: v })}
            />
            <Toggle 
              label="Alert on Suspicious Activity" 
              checked={settings.monitoring.alertOnSuspiciousActivity}
              onChange={v=> updateMonitoring({ alertOnSuspiciousActivity: v })}
              description="Multiple failed logins, unusual locations"
            />
            <Toggle 
              label="Geo-Location Tracking" 
              checked={settings.monitoring.geoLocationTracking}
              onChange={v=> updateMonitoring({ geoLocationTracking: v })}
              description="Track login locations for anomaly detection"
            />
          </div>
          <div>
            <Toggle 
              label="Device Fingerprinting" 
              checked={settings.monitoring.deviceFingerprinting}
              onChange={v=> updateMonitoring({ deviceFingerprinting: v })}
              description="Identify devices for security analysis"
            />
            <Toggle 
              label="Email Notifications" 
              checked={settings.monitoring.emailNotifications}
              onChange={v=> updateMonitoring({ emailNotifications: v })}
              description="Send security alerts via email"
            />
            <label className="block py-2">
              <div className="text-sm font-medium text-slate-700 mb-1">Admin Email for Alerts</div>
              <input 
                type="email" 
                value={settings.monitoring.adminEmailAlerts} 
                onChange={e=> updateMonitoring({ adminEmailAlerts: e.target.value })}
                className="w-full h-8 px-2 rounded border border-slate-300 text-sm"
              />
            </label>
          </div>
        </div>
      </SecuritySection>
      
      <SecuritySection title="Compliance & Data Protection" description="Configure regulatory compliance settings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Toggle 
              label="GDPR Compliant" 
              checked={settings.compliance.gdprCompliant}
              onChange={v=> updateCompliance({ gdprCompliant: v })}
              description="Enable GDPR data protection features"
            />
            <Toggle 
              label="Encryption at Rest" 
              checked={settings.compliance.encryptionAtRest}
              onChange={v=> updateCompliance({ encryptionAtRest: v })}
              description="Encrypt stored data"
            />
            <Toggle 
              label="Encryption in Transit" 
              checked={settings.compliance.encryptionInTransit}
              onChange={v=> updateCompliance({ encryptionInTransit: v })}
              description="Encrypt data transmission"
            />
          </div>
          <div>
            <NumberInput 
              label="Data Retention Period" 
              value={settings.compliance.dataRetention} 
              onChange={v=> updateCompliance({ dataRetention: v })}
              min={30}
              max={2555}
              suffix="days"
            />
            <NumberInput 
              label="Audit Log Retention" 
              value={settings.compliance.auditLogRetention} 
              onChange={v=> updateCompliance({ auditLogRetention: v })}
              min={365}
              max={3650}
              suffix="days"
            />
          </div>
        </div>
      </SecuritySection>
      
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 text-sm text-slate-600">
        <strong>Note:</strong> Changes are automatically saved. Some settings may require user logout/login to take effect. 
        Consult your security team before modifying production settings.
      </div>
    </div>
  );
}